<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <!-- Slim mode: icon only with popup menu -->
  <div
    v-if="isSlimMode"
    class="sidebar-profile-section sidebar-profile-slim"
    :class="`position-${position}`"
  >
    <button
      class="profile-avatar-btn"
      v-tooltip.right="displayName || userId"
      @click="toggleSlimMenu"
      aria-haspopup="true"
      aria-controls="sidebar_profile_menu"
    >
      <i class="ri-user-line" />
    </button>
    <PrimeMenu
      ref="slimMenu"
      id="sidebar_profile_menu"
      :model="slimMenuItems"
      popup
    />
  </div>

  <!-- Extended mode: full profile with expandable menu -->
  <div v-else class="sidebar-profile-section" :class="`position-${position}`">
    <div class="profile-header" @click="toggleMenu">
      <div class="profile-avatar">
        <i class="ri-user-line" />
      </div>
      <div class="profile-info">
        <span class="profile-name">{{ displayName || userId }}</span>
        <span v-if="jobTitle" class="profile-job">{{ jobTitle }}</span>
      </div>
      <i class="ri-arrow-down-s-line profile-arrow" :class="{ expanded }" />
    </div>
    <transition name="submenu">
      <div v-show="expanded" class="profile-menu">
        <router-link to="/profile" class="profile-menu-item">
          <i class="ri-user-settings-line" />
          <span>{{ t("sidebarMenu.profile") }}</span>
        </router-link>
        <a v-if="portalUrl" :href="portalUrl" class="profile-menu-item">
          <i class="ri-home-smile-line" />
          <span>{{ t("sidebarMenu.portal") }}</span>
        </a>
        <button class="profile-menu-item" @click="handleLogout()">
          <i class="ri-logout-box-line" />
          <span>{{ t("sidebarMenu.logout") }}</span>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import PrimeMenu from "primevue/menu";
import { useLayout } from "@/composables/useLayout";
import { useUserStore } from "@/stores/user";
import { useConfigStore } from "@/stores/config";
import { useAuth } from "@/composables/useAuth";

export default {
  name: "SidebarProfileSection",
  components: {
    PrimeMenu,
  },
  props: {
    /**
     * Whether sidebar is in slim/collapsed mode
     */
    isSlimMode: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const router = useRouter();
    const { t } = useI18n();
    const { layoutState } = useLayout();
    const userStore = useUserStore();
    const configStore = useConfigStore();
    const { handleLogout } = useAuth();
    const expanded = ref(false);
    const slimMenu = ref();

    const position = computed(() => layoutState.sidebarProfilePosition);

    const displayName = computed(() => userStore.displayName || "");
    const userId = computed(() => userStore.person?.id || "");
    const jobTitle = computed(() => userStore.person?.jobTitle || "");

    const portalUrl = computed(() => {
      if (!configStore.PORTAIL_HOST) {
        return "";
      }
      return `${configStore.PORTAIL_HOST}/portail`;
    });

    const toggleMenu = () => {
      expanded.value = !expanded.value;
    };

    const toggleSlimMenu = (event) => {
      slimMenu.value.toggle(event);
    };

    const slimMenuItems = computed(() => {
      const items = [
        {
          label: t("sidebarMenu.profile"),
          icon: "ri-user-settings-line",
          command: () => {
            router.push("/profile");
          },
        },
      ];
      if (portalUrl.value) {
        items.push({
          label: t("sidebarMenu.portal"),
          icon: "ri-home-smile-line",
          command: () => {
            window.location.href = portalUrl.value;
          },
        });
      }
      items.push({
        label: t("sidebarMenu.logout"),
        icon: "ri-logout-box-line",
        command: () => {
          handleLogout();
        },
      });
      return items;
    });

    return {
      t,
      expanded,
      slimMenu,
      position,
      displayName,
      userId,
      jobTitle,
      portalUrl,
      toggleMenu,
      toggleSlimMenu,
      slimMenuItems,
      handleLogout,
    };
  },
};
</script>

<style lang="scss" scoped>
.sidebar-profile-section {
  &.position-end {
    border-top: 1px solid var(--p-content-border-color);
  }

  &.position-start {
    border-bottom: 1px solid var(--p-content-border-color);
  }
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--p-content-hover-background);
  }
}

.profile-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background-color: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
  flex-shrink: 0;

  i {
    font-size: 1.125rem;
  }
}

.profile-info {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.profile-name {
  font-weight: 600;
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--p-text-color);
}

.profile-job {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-arrow {
  flex-shrink: 0;
  font-size: 1rem;
  color: var(--p-text-muted-color);
  transition: transform 0.3s ease;

  &.expanded {
    transform: rotate(180deg);
  }
}

.profile-menu {
  overflow: hidden;
}

.profile-menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem 0.5rem 1.75rem;
  width: 100%;
  border: none;
  background-color: transparent;
  color: inherit;
  font-size: 0.875rem;
  font-family: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--p-content-hover-background);
  }

  i {
    font-size: 1rem;
    color: var(--p-text-muted-color);
  }

  span {
    color: var(--p-text-color);
  }
}

// Slim mode styles
.sidebar-profile-slim {
  display: flex;
  justify-content: center;
  padding: 0.75rem 0;
}

.profile-avatar-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  border: none;
  background-color: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }

  i {
    font-size: 1.125rem;
  }
}

// Submenu transition (matches AppSubmenu.vue)
.submenu-enter-active,
.submenu-leave-active {
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease;
  max-height: 200px;
  overflow: hidden;
}

.submenu-enter-from,
.submenu-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
